import type { ExplorerItem, ExplorerSection } from "./explorerData";
import {
  canStageAs,
  inputEdited,
  isSchemaEditorId,
  isSuiteRowId,
  parseSuiteRowId,
  SCHEMA_EDITOR_ID,
  type StagedKind,
  type StagedResource,
  type StagedRole,
  type StagedSchema,
  type StageTabId,
  type StudioView,
  schemaIdOfInstance,
  stageAcceptsDrop,
} from "./stagingModel";

// The AI Helper's picture of the Studio — a text block appended to the
// system prompt on every request (the engine's ContextItem enum is closed,
// so the prompt is the only frontend-only carrier). One line per explorer
// node, in the panel's own order, with the ids the helper's actions use
// and the roles each row can be staged into right now.

export type StageRoleName =
  | "source"
  | "transform"
  | "sink"
  | "schema"
  | "tests";

export type PipelineDescriptor =
  | { kind: "scratch" }
  | { kind: "draft"; name: string }
  | { kind: "deployed"; name: string; state: string };

/** The staged pipeline as the actions see it: the rows per role (with
 *  their ids), the suite, the selection, the Output panel and the last
 *  run — plus what blocks a run right now. */
export interface StagedSnapshot {
  sources: StagedResource[];
  transforms: StagedResource[];
  sinks: StagedResource[];
  schemas: StagedSchema[];
  tests: StagedResource[];
  suiteName: string | null;
  selectedId: string | null;
  outputCollapsed: boolean;
  lastRun: { name: string; success: boolean } | null;
  runBlockReason: string | null;
  runningId: string | null;
  /** The Source input document and the sample it was loaded from. */
  input: { document: string; origin: string | null };
  /** The Full pipeline editor holds no text (the template may go in). */
  codeEmpty: boolean;
  /** The pipeline code text as the Full view shows it; null in Stages. */
  code: string | null;
}

export interface StudioState {
  view: StudioView;
  tab: StageTabId;
  editsLocked: boolean;
  pipeline: PipelineDescriptor;
  filter: string;
  expanded: ReadonlySet<string>;
  collapsedSections: ReadonlySet<string>;
  sections: ExplorerSection[];
  roleOf: (id: string) => StagedKind | null;
  sources: ReadonlyArray<{ id: string }>;
  stagedSuite: { suiteId: string | null } | null;
  /** Staged source/sink ids whose drop-time schema seed is still
   *  resolving (schemaSeeding) — a switch to the Full view waits on them,
   *  as the toolbar's does. */
  seedingStageIds: readonly string[];
  staged: StagedSnapshot;
}

export interface Droppable {
  roles: StageRoleName[];
  staged?: StagedKind;
  reason?: string;
}

const MAX_CHILDREN = 60;
const VIEW_ONLY = "running pipeline is view-only";
const FULL_VIEW = "switch to Stages to stage items";

const STAGE_ROLES: StagedRole[] = ["source", "transform", "sink"];

function resourceRoles(item: ExplorerItem, state: StudioState): Droppable {
  const roles = STAGE_ROLES.filter((role) => canStageAs(role, item.provider));
  const sourceBlocked =
    roles.includes("source") && !stageAcceptsDrop("source", state.sources);
  const open = sourceBlocked ? roles.filter((r) => r !== "source") : roles;
  if (open.length === 0) {
    return {
      roles: [],
      reason: sourceBlocked ? "source slot taken" : "not stageable",
    };
  }
  return sourceBlocked
    ? { roles: open, reason: "source slot taken" }
    : { roles: open };
}

function suiteRoles(item: ExplorerItem, state: StudioState): Droppable {
  if (state.stagedSuite === null) return { roles: ["tests"] };
  const staged = parseSuiteRowId(item.id) === state.stagedSuite.suiteId;
  return staged
    ? { roles: [], staged: "test" }
    : { roles: [], reason: "unstage the staged suite first" };
}

/** The roles an explorer row can be staged into right now — the drag
 *  rules (canStageAs, the single source slot, the one-suite lock) minus
 *  the tab restriction, because a stage action switches the tab itself. */
export function droppableFor(
  item: ExplorerItem,
  state: StudioState,
): Droppable {
  if (state.editsLocked) return { roles: [], reason: VIEW_ONLY };
  if (state.view === "full") return { roles: [], reason: FULL_VIEW };
  if (isSuiteRowId(item.id)) return suiteRoles(item, state);
  const staged = state.roleOf(item.id);
  if (item.id.startsWith("schema:")) {
    return staged ? { roles: ["schema"], staged } : { roles: ["schema"] };
  }
  if (staged) return { roles: [], staged };
  return resourceRoles(item, state);
}

function droppableText(d: Droppable): string {
  if (d.staged) {
    return d.roles.length > 0
      ? `staged: ${d.staged} · droppable: ${d.roles.join(", ")}`
      : `staged: ${d.staged}`;
  }
  if (d.roles.length === 0) return `not droppable: ${d.reason ?? "no role"}`;
  const roles = `droppable: ${d.roles.join(", ")}`;
  return d.reason ? `${roles} (${d.reason})` : roles;
}

function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? "" : "s"}`;
}

function leafLine(
  item: ExplorerItem,
  state: StudioState,
  indent: string,
): string {
  const drop = droppableText(droppableFor(item, state));
  if (isSuiteRowId(item.id)) {
    const cases = plural(item.count ?? 0, "case");
    return `${indent}${item.id}  ${item.name} (${cases}) · ${drop}`;
  }
  if (item.id.startsWith("schema:")) {
    const label = item.labelPrefix
      ? `${item.labelPrefix} - ${item.name}`
      : item.name;
    return `${indent}${item.id}  ${label} · ${drop}`;
  }
  const kind = item.resourceType ? ` (${item.resourceType})` : "";
  return `${indent}${item.id}  ${item.name}${kind} · ${drop}`;
}

function nodeStatus(item: ExplorerItem, state: StudioState): string {
  if (item.onRetry) return "stalled (Retry available)";
  if (item.childrenLoading) return "loading";
  return state.expanded.has(item.id) ? "expanded" : "collapsed";
}

function isBranch(item: ExplorerItem): boolean {
  return !!item.children || !!item.childrenLoading || !!item.onRetry;
}

function capped(total: number, indent: string): string[] {
  return total > MAX_CHILDREN
    ? [`${indent}… and ${total - MAX_CHILDREN} more (filter to narrow)`]
    : [];
}

function branchLines(
  item: ExplorerItem,
  state: StudioState,
  indent: string,
): string[] {
  const unit = item.id.startsWith("schemas:") ? "schema" : "resource";
  const count = item.count != null ? ` ${plural(item.count, unit)}` : "";
  const head = `${indent}${item.name} [${item.id}]${count} · ${nodeStatus(item, state)}`;
  const children = item.children ?? [];
  const shown = children
    .slice(0, MAX_CHILDREN)
    .map((c) => leafLine(c, state, `${indent}  `));
  return [head, ...shown, ...capped(children.length, `${indent}  `)];
}

function sectionCount(section: ExplorerSection): string {
  if (section.count == null) return "";
  return section.id.startsWith("provider:")
    ? ` ${plural(section.count, "service")}`
    : ` ${section.count}`;
}

function sectionLines(section: ExplorerSection, state: StudioState): string[] {
  const status = state.collapsedSections.has(section.id)
    ? "collapsed"
    : "expanded";
  const head = `${section.label} [${section.id}]${sectionCount(section)} · ${status}`;
  const items = section.items
    .slice(0, MAX_CHILDREN)
    .flatMap((item) =>
      isBranch(item)
        ? branchLines(item, state, "  ")
        : [leafLine(item, state, "  ")],
    );
  return [head, ...items, ...capped(section.items.length, "  ")];
}

function pipelineText(p: PipelineDescriptor): string {
  if (p.kind === "scratch") return "scratch (unsaved)";
  if (p.kind === "draft") return `draft "${p.name}"`;
  return `deployed "${p.name}" (${p.state})`;
}

/** The schema a staged step carries, if any — the block's first one. */
export function schemaAttachedTo(
  schemas: StagedSchema[],
  role: StagedRole,
  id: string,
): StagedSchema | undefined {
  return schemas.find((s) =>
    s.attachments.some((a) => a.role === role && a.id === id),
  );
}

function stageLine(
  role: StagedRole,
  item: StagedResource,
  schemas: StagedSchema[],
): string {
  const schema = schemaAttachedTo(schemas, role, item.id);
  const withSchema = schema ? ` · schema: ${schema.id}` : "";
  return `  ${role}: ${item.id}  ${item.name}${withSchema}`;
}

function roleLines(
  role: StagedRole,
  items: StagedResource[],
  schemas: StagedSchema[],
): string[] {
  if (items.length === 0) return [`  ${role}: (none)`];
  return items.map((item) => stageLine(role, item, schemas));
}

function testsLine(staged: StagedSnapshot): string {
  if (staged.suiteName === null) return "  tests: (none)";
  const cases = staged.tests.map((t) => `${t.id} ${JSON.stringify(t.name)}`);
  return `  tests: suite ${JSON.stringify(staged.suiteName)}${cases.map((c) => ` · ${c}`).join("")}`;
}

function lastRunText(run: StagedSnapshot["lastRun"]): string {
  if (!run) return "none";
  return `${run.name} ${run.success ? "ok" : "failed"}`;
}

function inputShape(document: string): string {
  try {
    return Array.isArray(JSON.parse(document)) ? "array" : "object";
  } catch {
    return "invalid JSON";
  }
}

function inputLine(input: StagedSnapshot["input"]): string {
  if (input.document.trim() === "") return "  input: empty";
  const state = inputEdited(input.document, input.origin)
    ? "edited"
    : "loaded sample";
  return `  input: ${inputShape(input.document)} · ${state}`;
}

// A schema instance's block, in the model's words: the stage it is
// attached to, or the tab whose bottom block holds it.
function schemaBlockText(blockKey: string): string {
  const rest = blockKey.slice(SCHEMA_EDITOR_ID.length + 1);
  if (blockKey === SCHEMA_EDITOR_ID) return "sink tab's schema block";
  if (rest.startsWith("@")) return `${rest.slice(1)} tab's schema block`;
  return `on ${rest}`;
}

/** The selection as the helper should name it: a staged id, a schema by
 *  its id and block, or "none" for the editor pseudo-rows — never the
 *  Studio's internal instance/editor ids. */
export function selectionText(selectedId: string | null): string {
  if (selectedId === null) return "none";
  const schemaId = schemaIdOfInstance(selectedId);
  if (schemaId !== null) {
    const blockKey = selectedId.slice(schemaId.length + 1);
    return `${schemaId} (${schemaBlockText(blockKey)})`;
  }
  if (isSchemaEditorId(selectedId) || selectedId.endsWith(":editor")) {
    return "none (editor)";
  }
  return selectedId;
}

function pipelineLines(staged: StagedSnapshot): string[] {
  const output = staged.outputCollapsed ? "collapsed" : "shown";
  const blocked = staged.runBlockReason
    ? ` · run blocked: ${staged.runBlockReason}`
    : "";
  const head = `pipeline · selected: ${selectionText(staged.selectedId)} · output panel: ${output} · last run: ${lastRunText(staged.lastRun)}${blocked}`;
  return [
    head,
    inputLine(staged.input),
    ...roleLines("source", staged.sources, staged.schemas),
    ...roleLines("transform", staged.transforms, staged.schemas),
    ...roleLines("sink", staged.sinks, staged.schemas),
    testsLine(staged),
  ];
}

const MAX_CODE_LINES = 200;

// The Full view's document, as the user sees it — the engine's context
// items have no code kind, so it rides in the snapshot, capped like they
// are.
function codeLines(state: StudioState): string[] {
  const code = state.view === "full" ? state.staged.code : null;
  if (code === null || code.trim() === "") return [];
  const lines = code.split("\n");
  const shown = lines.slice(0, MAX_CODE_LINES);
  const more = lines.length - shown.length;
  return [
    "pipeline code:",
    ...shown,
    ...(more > 0 ? [`… ${more} more lines`] : []),
  ];
}

export function buildStudioSnapshot(state: StudioState): string {
  const edits = state.editsLocked ? "locked" : "allowed";
  const explorer = state.editsLocked ? `hidden (${VIEW_ONLY})` : "visible";
  const lines = [
    "<studio_state>",
    `view: ${state.view} · tab: ${state.tab} · pipeline: ${pipelineText(state.pipeline)} · edits: ${edits}`,
    ...pipelineLines(state.staged),
    ...codeLines(state),
    `explorer: ${explorer} · filter: ${JSON.stringify(state.filter)}`,
    ...state.sections.flatMap((s) => sectionLines(s, state)),
    "</studio_state>",
  ];
  return lines.join("\n");
}
